import React from 'react';
import { ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ErrorBar, ReferenceArea, ReferenceLine, Label } from 'recharts';
import { FigureCard } from './FigureCard';
const data = [{
  t: 60,
  e: 22.4,
  err: 1.8
}, {
  t: 70,
  e: 41.7,
  err: 2.3
}, {
  t: 80,
  e: 77.5,
  err: 3.1
}, {
  t: 90,
  e: 129.8,
  err: 4.7
}, {
  t: 100,
  e: 198.3,
  err: 6.2
}];
export function Figure1() {
  return <FigureCard number={1} title="Effect of Heat Source Temperature on Total Electrical Energy Output" subtitle="Mean of 3 trials per temperature. Error bars represent ±1 standard deviation." caption="Total electrical energy E_out rises non-linearly with heat source temperature. The accelerating trend is consistent with P ∝ V² ∝ ω², where motor angular velocity scales with steam pressure (and thus T_hot)." data-id="element-52">
      <div className="relative" data-id="element-53">
        <div className="h-[440px] w-full" data-id="element-54">
          <ResponsiveContainer data-id="element-55">
            <ComposedChart data={data} margin={{
            top: 30,
            right: 60,
            left: 30,
            bottom: 50
          }} data-id="element-56">
              <defs data-id="element-57">
                <linearGradient id="f1Fill" x1="0" y1="0" x2="0" y2="1" data-id="element-58">
                  <stop offset="0%" stopColor="#8B2500" stopOpacity={0.28} data-id="element-59" />
                  <stop offset="100%" stopColor="#8B2500" stopOpacity={0.04} data-id="element-60" />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#E5E0D6" strokeDasharray="2 4" vertical={false} opacity={0.6} data-id="element-61" />
              <XAxis dataKey="t" type="number" domain={[55, 105]} ticks={[60, 70, 80, 90, 100]} tickLine={{
              stroke: '#9B9384'
            }} axisLine={{
              stroke: '#1B1B1B'
            }} tick={{
              fontSize: 12,
              fill: '#374151'
            }} data-id="element-62">
                <Label value="Heat Source Temperature, T_hot (°C)" position="bottom" offset={20} style={{
                fill: '#1B1B1B',
                fontSize: 13,
                fontFamily: 'Inter',
                fontWeight: 500
              }} data-id="element-63" />
              </XAxis>
              <YAxis domain={[0, 220]} ticks={[0, 50, 100, 150, 200]} tickLine={{
              stroke: '#9B9384'
            }} axisLine={{
              stroke: '#1B1B1B'
            }} tick={{
              fontSize: 12,
              fill: '#374151'
            }} data-id="element-64">
                <Label value="Total Electrical Energy Output, E_out (μJ)" angle={-90} position="left" offset={10} style={{
                fill: '#1B1B1B',
                fontSize: 13,
                fontFamily: 'Inter',
                fontWeight: 500,
                textAnchor: 'middle'
              }} data-id="element-65" />
              </YAxis>
              <Tooltip contentStyle={{
              background: '#fff',
              border: '1px solid #8B2500',
              fontFamily: 'Inter',
              fontSize: 12
            }} formatter={(v: any) => [`${v} μJ`, 'E_out']} labelFormatter={l => `T_hot = ${l} °C`} data-id="element-66" />
              <Area type="monotone" dataKey="e" stroke="none" fill="url(#f1Fill)" isAnimationActive={false} data-id="element-67" />
              <Line type="monotone" dataKey="e" stroke="#8B2500" strokeWidth={2.5} dot={{
              r: 6,
              fill: '#8B2500',
              stroke: '#fff',
              strokeWidth: 2
            }} activeDot={{
              r: 8
            }} isAnimationActive={false} data-id="element-68">
                <ErrorBar dataKey="err" width={8} strokeWidth={1.5} stroke="#8B2500" data-id="element-69" />
              </Line>
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Callouts overlay */}
        <div className="absolute top-[60px] right-[100px] pointer-events-none" data-id="element-70">
          <div className="bg-white border border-[#8B2500] px-3 py-1.5 rounded-sm shadow-sm" data-id="element-71">
            <div className="font-sans text-[11px] text-[#8B2500] font-semibold" data-id="element-72">
              198.3 μJ
            </div>
          </div>
        </div>
        <div className="absolute bottom-[110px] left-[110px] pointer-events-none" data-id="element-73">
          <div className="bg-white border border-[#8B2500] px-3 py-1.5 rounded-sm shadow-sm" data-id="element-74">
            <div className="font-sans text-[11px] text-[#8B2500] font-semibold" data-id="element-75">
              22.4 μJ
            </div>
          </div>
        </div>

        {/* Non-linear annotation */}
        <div className="absolute top-[150px] right-[180px] pointer-events-none max-w-[180px]" data-id="element-76">
          <div className="bg-[#FEF3C7] border border-[#EF9F27] px-3 py-2 rounded-sm shadow-sm" data-id="element-77">
            <div className="font-sans text-[10px] text-[#7C5A00] font-semibold uppercase tracking-wider mb-1" data-id="element-78">
              Non-linear growth
            </div>
            <div className="font-mono text-[11px] text-[#1B1B1B]" data-id="element-79">
              P ∝ V² ∝ ω²
            </div>
          </div>
        </div>

        {/* 8.9× bracket */}
        <div className="flex items-center justify-center gap-3 mt-4 px-12" data-id="element-80">
          <div className="h-px flex-1 bg-[#8B2500]" data-id="element-81" />
          <span className="font-sans text-[12px] text-[#8B2500] font-semibold tracking-wide" data-id="element-82">
            8.9× increase across 60°C → 100°C
          </span>
          <div className="h-px flex-1 bg-[#8B2500]" data-id="element-83" />
        </div>

        {/* Legend + R² note */}
        <div className="flex justify-between items-end mt-4 px-2" data-id="element-84">
          <div className="flex items-center gap-5 text-[11px] font-sans text-[#374151]" data-id="element-85">
            <span className="flex items-center gap-2" data-id="element-86">
              <span className="w-2.5 h-2.5 rounded-full bg-[#8B2500] inline-block" data-id="element-87" />
              Mean E_out
            </span>
            <span className="flex items-center gap-2" data-id="element-88">
              <span className="inline-block text-[#8B2500] font-bold" data-id="element-89">┤├</span>
              ±1 SD
            </span>
          </div>
          <div className="font-serif italic text-[11px] text-[#5B5448]" data-id="element-90">
            R² trend consistent with quadratic scaling
          </div>
        </div>
      </div>
    </FigureCard>;
}